import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Collapse, IconButton, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VideocamIcon from '@mui/icons-material/Videocam';
import useStore from '../../store/useStore';
import AssessmentVideoRecorder from '../AssessmentVideoRecorder';
import AssessmentVideoList from './AssessmentVideoList';
import PendingClipsPanel from './PendingClipsPanel';
import useClipQueue from './useClipQueue';
import { listStudentVideos } from './videoApi';

// «Видео» block. With `studentId` it lists every clip of the student (all
// assessments — teachers, admins and the child's parents can watch); without
// it, the clips of one profile. Recording / uploading goes into `profileId`
// when `canRecord` (the latest version of the open assessment).
export default function ProfileVideosSection({
  profileId,
  studentId,
  canRecord,
}) {
  const { language } = useStore();
  const en = language === 'en';
  const [open, setOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [count, setCount] = useState(null);
  const targetRef = useRef(profileId);

  const onUploaded = useCallback(
    (asset, target) => {
      if (studentId || target === targetRef.current) {
        setOpen(true);
        setRefreshKey((n) => n + 1);
      }
    },
    [studentId]
  );

  // Student mode: show the count before the list is opened
  useEffect(() => {
    if (!studentId) return undefined;
    let cancelled = false;
    listStudentVideos(studentId)
      .then((list) => {
        if (!cancelled) setCount(list.length);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [studentId, refreshKey]);
  const queue = useClipQueue(profileId, { onUploaded });

  // The profile a clip belongs to is the one that was open when recording began
  const clipProfileRef = useRef(null);
  targetRef.current = profileId;

  const pending = queue.clips.filter((c) => c.status !== 'done');

  return (
    <Box
      sx={{
        mb: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        px: 1.5,
        py: 0.75,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <VideocamIcon fontSize="small" color="action" />
        <Typography sx={{ fontWeight: 700, flex: 1 }}>
          {en ? 'Video' : 'Видео'}
          {(open || studentId) && count > 0 ? ` (${count})` : ''}
        </Typography>
        {canRecord && profileId && (
          <AssessmentVideoRecorder
            trigger="button"
            triggerLabel={en ? 'Record' : 'Записать'}
            onOpen={() => {
              clipProfileRef.current = profileId;
            }}
            onClip={(clip) =>
              queue.add({ ...clip, profileId: clipProfileRef.current })
            }
          />
        )}
        <IconButton
          size="small"
          onClick={() => setOpen((v) => !v)}
          aria-label={en ? 'Show videos' : 'Показать видео'}
        >
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      {pending.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <PendingClipsPanel
            clips={pending}
            onRetry={queue.retry}
            onDiscard={queue.discard}
          />
        </Box>
      )}
      <Collapse in={open} unmountOnExit>
        <Box sx={{ mt: 1, pb: 0.75 }}>
          <AssessmentVideoList
            profileId={studentId ? undefined : profileId}
            studentId={studentId}
            canEdit={canRecord}
            refreshKey={refreshKey}
            onCountChange={setCount}
          />
        </Box>
      </Collapse>
    </Box>
  );
}
